import { useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { EmptyStateCard } from '../EmptyStateCard';
import { useGetAssetComparisonQuery, useGetAssetsQuery } from '../../api/emissions';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { ASSET_TYPE_BRANDING } from '../../config/assetTypeBranding';
import { SCOPE_COLORS } from '../../config/scopeBranding';
import { AssetType, type AssetComparisonItem } from '../../types/emissions';

const SCOPE_LABELS: Record<string, string> = {
  scope1: 'Scope 1',
  scope2: 'Scope 2',
  scope3: 'Scope 3',
};

interface AssetComparisonPanelProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  assetType?: AssetType | 'unassigned';
  includeChildren?: boolean;
}

export const AssetComparisonPanel = ({ initiativeId, periodStart, periodEnd, assetType, includeChildren }: AssetComparisonPanelProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const { data, isLoading: comparisonLoading, error } = useGetAssetComparisonQuery({ initiativeId, periodStart, periodEnd, includeChildren });
  const { data: allAssetsResponse, isLoading: assetsLoading } = useGetAssetsQuery({ initiativeId, limit: 100, includeChildren });
  const isLoading = comparisonLoading || assetsLoading;

  // Merge: show ALL assets + unassigned, filling zero-emission placeholders for missing assets
  const assets = useMemo(() => {
    const comparisonAssets = data?.assets ?? [];
    const allAssets = allAssetsResponse?.data ?? [];

    const comparisonMap = new Map(comparisonAssets.map((a) => [a._id, a]));
    const merged: AssetComparisonItem[] = allAssets.map((asset) => {
      const existing = comparisonMap.get(asset._id);
      if (existing) return existing;
      return {
        _id: asset._id,
        name: asset.name,
        assetType: asset.assetType,
        scopes: [],
        activityTypes: [],
        totalCo2e: 0,
        totalActivities: 0,
      };
    });

    // Include "Unassigned" entry from comparison data if present
    const unassigned = comparisonAssets.find((a) => a._id === 'unassigned');
    if (unassigned) {
      merged.push(unassigned);
    }

    // Sort: assets with emissions first (descending), then zero-emission assets alphabetically
    merged.sort((a, b) => {
      if (a.totalCo2e > 0 && b.totalCo2e === 0) return -1;
      if (a.totalCo2e === 0 && b.totalCo2e > 0) return 1;
      if (a.totalCo2e > 0 && b.totalCo2e > 0) return b.totalCo2e - a.totalCo2e;
      return a.name.localeCompare(b.name);
    });

    if (!assetType) return merged;
    if (assetType === 'unassigned') return merged.filter((a) => a._id === 'unassigned');
    return merged.filter((a) => a.assetType === assetType);
  }, [data?.assets, allAssetsResponse?.data, assetType]);

  const scopes = useMemo(() => {
    const scopeSet = new Set<string>();
    assets.forEach((asset) => {
      asset.scopes.forEach((s) => scopeSet.add(s.scope));
    });
    return ['scope1', 'scope2', 'scope3'].filter((s) => scopeSet.has(s));
  }, [assets]);

  const totalCo2e = assets.reduce((sum, a) => sum + a.totalCo2e, 0);
  const totalFormatted = formatCompact(totalCo2e);

  // Single shared chart unit so all bars are comparable.
  // Derive the kg→chart-unit divisor from the total's formatted value.
  const chartUnitDivisor = totalCo2e > 0 ? totalCo2e / totalFormatted.value : 1;
  const chartUnitLabel = totalFormatted.unitCompact;

  const toChartValue = useCallback(
    (valueInKg: number): number => valueInKg / chartUnitDivisor,
    [chartUnitDivisor]
  );

  const formatChartTick = useCallback((value: number): string => {
    if (!Number.isFinite(value)) return '0';
    const abs = Math.abs(value);
    const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }, []);

  const getScopeValue = useCallback(
    (asset: AssetComparisonItem, scope: string): number => {
      const scopeData = asset.scopes.find((s) => s.scope === scope);
      return scopeData ? toChartValue(scopeData.co2e) : 0;
    },
    [toChartValue]
  );

  const rechartsData = useMemo(() => {
    return assets.map((asset) => {
      const row: Record<string, string | number> = { name: asset.name || 'Unknown' };
      scopes.forEach((scope) => {
        row[scope] = getScopeValue(asset, scope);
      });
      return row;
    });
  }, [assets, scopes, getScopeValue]);

  const barChartConfig = useMemo((): ChartConfig => {
    const config: ChartConfig = {};
    scopes.forEach((scope) => {
      config[scope] = { label: SCOPE_LABELS[scope] || scope, color: SCOPE_COLORS[scope] || '#6c757d' };
    });
    return config;
  }, [scopes]);

  if (isLoading) {
    return (
      <div className='tw:flex tw:items-center tw:justify-center tw:py-10'>
        <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='tw:p-4 tw:text-center'>
          <i className='fa fa-exclamation-triangle fa-2x tw:text-red-500 tw:mb-2 tw:opacity-50' />
          <p className='tw:text-muted-foreground tw:mb-0'>Failed to load asset comparison data</p>
        </CardContent>
      </Card>
    );
  }

  if (assets.length === 0) {
    return (
      <EmptyStateCard
        title='Asset Comparison'
        description='Emissions breakdown by asset and scope'
        message='No assets found. Create assets to see their emissions comparison.'
      />
    );
  }

  const chartHeight = Math.max(280, assets.length * 50);

  return (
    <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-12 tw:gap-4'>
      {/* Stacked Bar Chart */}
      <div className='tw:lg:col-span-7'>
        <Card className='tw:h-full'>
          <CardHeader>
            <CardTitle>Asset Comparison</CardTitle>
            <CardDescription>Emissions breakdown by asset ({assets.length} assets, {chartUnitLabel}CO₂e)</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={barChartConfig} className='tw:w-full' style={{ height: chartHeight }}>
              <BarChart layout='vertical' data={rechartsData} margin={{ top: 5, right: 0, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                <XAxis
                  type='number'
                  domain={[0, (dataMax: number) => dataMax * 1.05]}
                  tickFormatter={(v: number) => formatChartTick(v)}
                />
                <YAxis type='category' dataKey='name' width={110} tick={{ fontSize: 11 }} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => `${formatChartTick(Number(value))} ${chartUnitLabel} — ${name}`}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                {scopes.map((scope) => (
                  <Bar
                    key={scope}
                    dataKey={scope}
                    name={SCOPE_LABELS[scope]}
                    fill={SCOPE_COLORS[scope] || '#6c757d'}
                    stackId='a'
                  />
                ))}
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Asset Details Table */}
      <div className='tw:lg:col-span-5'>
        <Card className='tw:h-full'>
          <CardHeader>
            <CardTitle>Asset Details</CardTitle>
            <CardDescription>Total: {totalFormatted.display}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='tw:pr-2' style={{ height: Math.max(400, assets.length * 50), overflowY: 'auto' }}>
              {assets.map((asset, index) => {
                const assetFormatted = formatCompact(asset.totalCo2e);
                const percentage = totalCo2e > 0 ? ((asset.totalCo2e / totalCo2e) * 100).toFixed(1) : '0.0';
                const brand = ASSET_TYPE_BRANDING[asset.assetType as AssetType] ?? ASSET_TYPE_BRANDING[AssetType.Other];

                return (
                  <div
                    key={asset._id}
                    className={`tw:py-3 ${index < assets.length - 1 ? 'tw:border-b tw:border-ThemeBorderDefault' : ''}`}
                  >
                    <div className='tw:flex tw:justify-between tw:items-start tw:mb-2'>
                      <div className='tw:min-w-0 tw:flex-1 tw:mr-2'>
                        <span className='tw:font-medium tw:block tw:truncate'>{asset.name || 'Unknown'}</span>
                        {brand && (
                          <Badge variant='outline' className={`tw:text-xs ${brand.iconColor}`}>{brand.label}</Badge>
                        )}
                      </div>
                      <div className='tw:text-right'>
                        <span className='tw:font-bold'>{assetFormatted.display}</span>
                        <span className='text-ThemeTextLight tw:block'>{percentage}%</span>
                      </div>
                    </div>
                    <div className='tw:flex tw:gap-2 tw:flex-wrap'>
                      {asset.scopes.map((scope) => {
                        const scopeFormatted = formatCompact(scope.co2e);
                        return (
                          <div key={scope.scope} className='tw:flex tw:items-center tw:gap-1'>
                            <span
                              className='tw:rounded'
                              style={{ width: 8, height: 8, backgroundColor: SCOPE_COLORS[scope.scope] || '#6c757d' }}
                            />
                            <span className='text-ThemeTextLight tw:text-xs'>
                              {SCOPE_LABELS[scope.scope] || scope.scope}: {scopeFormatted.formatted} {scopeFormatted.unitCompact}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <Progress value={Number(percentage)} className='tw:mt-2 tw:h-2' indicatorClassName='tw:bg-emerald-900/80' />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
